import { FormattedMessage } from 'react-intl';

// Components
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Switch,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  SetupLabel,
  SetupTitle,
  SetupValue,
  SetupSwitch,
  AnnualRecertMonthPicker,
} from '../shared';
import {
  CA_IMAX_ERRORS,
  getHUDProjectNumberError,
  getHUDPropertyIdError,
  getIMaxIdError,
} from './identifierValidation';
import SetupGridItem from '../../../SetupGridItem';
import ProgramPassbookRate from '../ProgramPassbookRate';
import { TextInput } from '../../../BasicDetails/TextInput';
import CertificationPastDue from '../../components/CertificationPastDue';
import OnOffTypography from '../../../OnOffTypography';
import type { HOTMAConfig, PassbookRate } from '../../../types';

// Constants
import messages from './messages';
import messagesAffordableSetup from '../../messages';
import HUD_SUBSIDY_CODES from '../../../../../constants/hudSubsidyCodes';
import { removeNonNumericCharacters } from '../../../../../utils';

// Hooks
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useMemo } from 'react';
import { isNil } from 'ramda';
import { toMoment } from '@fortress-technology-solutions/fortress-component-library/utils';
import HUDSubsidyTypeSection from './HUDSubsidyTypeSection';

interface HUDType {
  id: string;
  name: string;
  code: string;
}

export interface HUDContract {
  id: ?string;
  contractNumber: ?string;
  eligibilityUniverseCode: ?('PRE_1981' | 'POST_1981');
  effectiveDate: ?string;
  expirationDate: ?string;
}

export interface HUDValues {
  HUDProjectNumber: string;
  HUDiMaxNumber?: ?string;
  HUDCAiMaxNumber?: ?string;
  HUDPropertyId?: ?string;
  hudPrimarySubsidyType: HUDType;
  hudSecondarySubsidyType: HUDType;
  primaryContracts: ?Array<HUDContract>;
  secondaryContracts: ?Array<HUDContract>;
  HUDCompleteGRCertsWithoutSigned59As: boolean;
  passbookRates: Array<PassbookRate>;
  autoSendToTRACS: boolean;
  autoRolloverIncomeAndAssets: boolean;
  HUDRepaymentCollectionFeePercentage: number;
  intl: Object;
  hotmaConfig: HOTMAConfig;
  certificationPastDueDefinition?: string;
}

type Props = {
  promptToaster: Function,
  isLoadingPassbookRateData: boolean,
  editMode: boolean,
  onChange: Function,
  values: HUDValues,
};

const HUDSetup = ({
  editMode,
  onChange,
  values,
  promptToaster,
  isLoadingPassbookRateData,
}: Props) => {
  const {
    HUDProjectNumber,
    HUDiMaxNumber,
    HUDCAiMaxNumber,
    HUDPropertyId,
    primaryContracts: rawPrimaryContracts,
    secondaryContracts: rawSecondaryContracts,
    hudPrimarySubsidyType,
    hudSecondarySubsidyType,
    HUDCompleteGRCertsWithoutSigned59As,
    autoRolloverIncomeAndAssets,
    HUDAnnualRecertMonth,
    passbookRates,
    autoSendToTRACS,
    HUDRepaymentCollectionFeePercentage,
    intl,
    flags: { grossRentEnhancements },
    hotmaConfig,
    certificationPastDueDefinition,
  } = values;

  const { hudManageVouchers, hudTracsIntegration } = useFlags();
  const tracsiMaxIdError = getIMaxIdError(HUDiMaxNumber);
  const caiMaxIdError = getIMaxIdError(HUDCAiMaxNumber, CA_IMAX_ERRORS);
  const hudProjectNumberError = getHUDProjectNumberError(
    HUDProjectNumber,
    hudPrimarySubsidyType?.code,
  );
  const hudPropertyIdError = getHUDPropertyIdError(HUDPropertyId);

  const onTrimmedChange =
    (format = (value) => value) =>
    ({ target: { name, value } }) =>
      onChange({
        target: { name, value: value ? format(value.trim()) : null },
      });

  const hasContractData = (contract: ?HUDContract): boolean => {
    if (!contract) return false;
    return Boolean(
      contract.contractNumber ||
        contract.eligibilityUniverseCode ||
        contract.effectiveDate ||
        contract.expirationDate,
    );
  };

  const primaryContracts = useMemo(() => {
    if (rawPrimaryContracts?.length > 0) {
      return editMode
        ? rawPrimaryContracts
        : rawPrimaryContracts.filter(hasContractData);
    }
    return [];
  }, [editMode, rawPrimaryContracts]);

  const secondaryContracts = useMemo(() => {
    if (rawSecondaryContracts?.length > 0) {
      return editMode
        ? rawSecondaryContracts
        : rawSecondaryContracts.filter(hasContractData);
    }
    return [];
  }, [editMode, rawSecondaryContracts]);

  // Repayment Agreement Collection Fee is not offered for Section 236 or BMIR properties
  const isExcludedSubsidyType =
    hudPrimarySubsidyType?.code === HUD_SUBSIDY_CODES.SECTION_236 ||
    hudPrimarySubsidyType?.code === HUD_SUBSIDY_CODES.BMIR ||
    hudSecondarySubsidyType?.code === HUD_SUBSIDY_CODES.SECTION_236 ||
    hudSecondarySubsidyType?.code === HUD_SUBSIDY_CODES.BMIR;

  const showRepaymentAgreementCollectionFee =
    hudManageVouchers && !isExcludedSubsidyType;

  const repaymentCollectionFeeValidationMessage = useMemo(() => {
    if (isNil(HUDRepaymentCollectionFeePercentage)) return undefined;
    if (HUDRepaymentCollectionFeePercentage < 0) {
      return messages.valueMustNotBeNegative;
    }
    if (HUDRepaymentCollectionFeePercentage > 20) {
      return messages.valueMustNotExceed20;
    }
    return undefined;
  }, [HUDRepaymentCollectionFeePercentage]);

  // Validate contract dates per-contract — one boolean per entry
  const primaryContractValidations = useMemo(() => {
    return primaryContracts.map(({ effectiveDate, expirationDate }) => {
      if (!effectiveDate || !expirationDate) return false;
      return toMoment(expirationDate).isBefore(toMoment(effectiveDate));
    });
  }, [primaryContracts]);

  const secondaryContractValidations = useMemo(() => {
    return secondaryContracts.map(({ effectiveDate, expirationDate }) => {
      if (!effectiveDate || !expirationDate) return false;
      return toMoment(expirationDate).isBefore(toMoment(effectiveDate));
    });
  }, [secondaryContracts]);

  const handleAutoSendToTRACSChange = (event: Object, value: boolean) => {
    onChange({
      target: { name: 'config.autoSendToTRACS', value },
    });
  };

  const handleHUDAnnualRecertMonthChange = (date: Object, validate: Object) => {
    onChange({
      target: { name: 'setup.HUDAnnualRecertMonth', value: date },
    });
  };

  return (
    <Grid container spacing={2}>
      <SetupTitle>
        <FormattedMessage {...messages.title} />
      </SetupTitle>

      <HUDSubsidyTypeSection
        editMode={editMode}
        hudPrimarySubsidyType={hudPrimarySubsidyType}
        hudSecondarySubsidyType={hudSecondarySubsidyType}
        intl={intl}
        onChange={onChange}
        primaryContractValidations={primaryContractValidations}
        primaryContracts={primaryContracts}
        secondaryContractValidations={secondaryContractValidations}
        secondaryContracts={secondaryContracts}
        showMinimumRent
      />

      {/* HUD Project Number */}
      {hudTracsIntegration ? (
        <>
          <SetupGridItem
            label={<FormattedMessage {...messages.HUProjectNumber} />}
            TooltipProps={{
              title: <FormattedMessage {...messages.hudProjectNumberTooltip} />,
            }}
          >
            <SetupValue
              editMode={editMode}
              name="setup.HUDProjectNumber"
              value={HUDProjectNumber || ''}
              placeholder=""
              onChange={onTrimmedChange((value) => value.toUpperCase())}
              editable={editMode}
              required
              validateFunction={(value) =>
                !getHUDProjectNumberError(value, hudPrimarySubsidyType?.code)
              }
              message={hudProjectNumberError ?? messages.HUProjectNumber}
            />
          </SetupGridItem>

          <SetupGridItem
            label={<FormattedMessage {...messages.hudPropertyId} />}
            TooltipProps={{
              title: <FormattedMessage {...messages.hudPropertyIdTooltip} />,
            }}
          >
            <SetupValue
              editMode={editMode}
              name="setup.HUDPropertyId"
              value={HUDPropertyId || ''}
              placeholder=""
              onChange={onTrimmedChange()}
              editable={editMode}
              required
              validateFunction={(value) => !getHUDPropertyIdError(value)}
              message={hudPropertyIdError ?? messages.hudPropertyId}
            />
          </SetupGridItem>
        </>
      ) : (
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.HUProjectNumber} />
          <SetupValue
            editMode={editMode}
            name="setup.HUDProjectNumber"
            value={HUDProjectNumber || ''}
            placeholder=""
            onChange={onChange}
            editable={editMode}
          />
        </Grid>
      )}

      {showRepaymentAgreementCollectionFee && (
        <SetupGridItem
          label={
            <FormattedMessage {...messages.repaymentAgreementCollectionFee} />
          }
          TooltipProps={{
            title: (
              <FormattedMessage
                {...messages.repaymentAgreementCollectionFeeTooltip}
              />
            ),
          }}
        >
          {editMode ? (
            <TextInput
              name="setup.HUDRepaymentCollectionFeePercentage"
              value={
                HUDRepaymentCollectionFeePercentage != null
                  ? removeNonNumericCharacters(
                      String(HUDRepaymentCollectionFeePercentage),
                    )
                  : '20'
              }
              editMode={editMode}
              placeholder="20"
              onChange={({ target: { name, value } }) => {
                const cleanedValue = removeNonNumericCharacters(
                  value,
                ).substring(0, 2);
                onChange({
                  target: {
                    name,
                    value: cleanedValue === '' ? null : cleanedValue,
                  },
                });
              }}
              type="number"
              maxLength={2}
              required={true}
              validateFunction={(value) => {
                if (!value || value === '') return true;
                const numValue = parseFloat(value);
                return !isNaN(numValue) && numValue >= 0 && numValue <= 20;
              }}
              message={repaymentCollectionFeeValidationMessage}
            />
          ) : (
            <Typography variant="bodyMedium">
              {HUDRepaymentCollectionFeePercentage ?? 20}
            </Typography>
          )}
        </SetupGridItem>
      )}

      {/* TRACS iMAX ID */}
      <Grid item xs={12} sm={6}>
        <SetupLabel
          {...messages.tracsiMaxId}
          htmlFor={editMode ? 'setup-HUDiMaxNumber' : undefined}
        />
        <SetupValue
          editMode={editMode}
          id="setup-HUDiMaxNumber"
          name="setup.HUDiMaxNumber"
          value={HUDiMaxNumber || ''}
          placeholder=""
          onChange={({ target: { name, value } }) =>
            onChange({ target: { name, value: value?.toUpperCase() ?? null } })
          }
          maxLength={10}
          required
          validateFunction={(value) => !getIMaxIdError(value)}
          message={tracsiMaxIdError ?? messages.tracsiMaxId}
        />
      </Grid>

      {/* CA iMAX ID */}
      <Grid item xs={12} sm={6}>
        <SetupLabel
          {...messages.caiMaxId}
          htmlFor={editMode ? 'setup-HUDCAiMaxNumber' : undefined}
        />
        <SetupValue
          editMode={editMode}
          id="setup-HUDCAiMaxNumber"
          name="setup.HUDCAiMaxNumber"
          value={HUDCAiMaxNumber || ''}
          placeholder=""
          onChange={({ target: { name, value } }) =>
            onChange({ target: { name, value: value?.toUpperCase() ?? null } })
          }
          maxLength={10}
          required
          validateFunction={(value) => !getIMaxIdError(value, CA_IMAX_ERRORS)}
          message={caiMaxIdError ?? messages.caiMaxId}
        />
      </Grid>

      {grossRentEnhancements && (
        <SetupGridItem
          label={
            <FormattedMessage
              {...messages.HUDCompleteGRCertsWithoutSigned59As}
            />
          }
          TooltipProps={{
            title: (
              <FormattedMessage
                {...messages.HUDCompleteGRCertsWithoutSigned59AsTooltip}
              />
            ),
          }}
        >
          <SetupSwitch
            switchId={'hud-complete-gr-certs-without-signed-59as-switch'}
            editMode={editMode}
            name="setup.HUDCompleteGRCertsWithoutSigned59As"
            disabled={!editMode}
            value={HUDCompleteGRCertsWithoutSigned59As}
            onChange={onChange}
          />
        </SetupGridItem>
      )}

      <SetupGridItem
        label={
          <FormattedMessage
            {...messagesAffordableSetup.annualRecertMonth}
            values={{ program: 'HUD' }}
          />
        }
      >
        <AnnualRecertMonthPicker
          handleAnnualRecertMonthChange={handleHUDAnnualRecertMonthChange}
          editMode={editMode}
          programName={'HUD'}
          value={HUDAnnualRecertMonth}
        />
      </SetupGridItem>

      <SetupGridItem
        label={<FormattedMessage {...messages.autoSendToTRACS} />}
        TooltipProps={{
          title: <FormattedMessage {...messages.autoSendToTRACSDescription} />,
        }}
      >
        {editMode ? (
          <Switch
            name="config.autoSendToTRACS"
            id="autoSendToTRACS-switch"
            checked={autoSendToTRACS}
            onChange={handleAutoSendToTRACSChange}
          />
        ) : (
          <OnOffTypography on={autoSendToTRACS}>
            {autoSendToTRACS ? (
              <FormattedMessage {...messages.on} />
            ) : (
              <FormattedMessage {...messages.off} />
            )}
          </OnOffTypography>
        )}
      </SetupGridItem>
      <SetupGridItem
        label={
          <FormattedMessage
            {...messagesAffordableSetup.autoRolloverIncomeAndAssets}
          />
        }
        TooltipProps={{
          title: (
            <FormattedMessage
              {...messagesAffordableSetup.autoRolloverIncomeAndAssetsDescription}
            />
          ),
        }}
      >
        <SetupSwitch
          switchId={'hud-auto-rollover-income-and-assets-switch'}
          editMode={editMode}
          name="setup.HUDAutoRolloverIncomeAndAssets"
          disabled={!editMode}
          value={autoRolloverIncomeAndAssets}
          onChange={onChange}
        />
      </SetupGridItem>

      <Grid item xs={12} sm={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messagesAffordableSetup.hotmaActive} />
            <OnOffTypography on={hotmaConfig?.hasAdoptedHOTMA ?? false}>
              {hotmaConfig?.hasAdoptedHOTMA ?? false ? (
                <FormattedMessage {...messagesAffordableSetup.on} />
              ) : (
                <FormattedMessage {...messagesAffordableSetup.off} />
              )}
            </OnOffTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messagesAffordableSetup.hotmaImplementationDate} />
            <SetupValue
              name="HUD.HOTMAEffectiveDate"
              value={
                hotmaConfig?.hasAdoptedHOTMA ?? false
                  ? hotmaConfig?.effectiveDate
                  : '---'
              }
              editable={false}
            />
          </Grid>
          <CertificationPastDue
            editMode={editMode}
            value={certificationPastDueDefinition}
            name="setup.HUDCertificationPastDueDefinition"
            onChange={onChange}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <ProgramPassbookRate
          key={'HUD_PassbookRates'}
          programName={'HUD'}
          isLoadingPassbookRateData={isLoadingPassbookRateData}
          passbookRates={passbookRates}
        />
      </Grid>
    </Grid>
  );
};

export default HUDSetup;
